/**
 * useMacros Hook - Wrapper for Macro Store
 * 
 * Provides an interface for App.js to manage nutrition goals and exclusions.
 */

import { useCallback } from 'react';
import { useMacroStore } from '../stores/macroStore';
import { useUI } from './useUI';
import type { Macros } from '../types';

export function useMacros(currentUser: any) {
    // Actions are stable in Zustand if retrieved via granular selectors
    const storeLoadMacrosAndExclusions = useMacroStore(state => state.loadMacrosAndExclusions);
    const storeUpdateMacros = useMacroStore(state => state.updateMacros);
    const storeAddExclusion = useMacroStore(state => state.addExclusion);
    const storeRemoveExclusion = useMacroStore(state => state.removeExclusion);
    const storeAutoUpdateMacros = useMacroStore(state => state.autoUpdateMacros);
    const storeAutoUpdateExclusions = useMacroStore(state => state.autoUpdateExclusions);
    const storeSetMacros = useMacroStore(state => state.setMacros);
    const storeSetTempMacros = useMacroStore(state => state.setTempMacros);
    const storeSetNewExclusion = useMacroStore(state => state.setNewExclusion);
    const storeSetActivePreset = useMacroStore(state => state.setActivePreset);
    const storeHandlePresetSelect = useMacroStore(state => state.handlePresetSelect);

    // State subscription
    const macros = useMacroStore(state => state.macros);
    const tempMacros = useMacroStore(state => state.tempMacros);
    const exclusions = useMacroStore(state => state.exclusions);
    const newExclusion = useMacroStore(state => state.newExclusion);
    const activePreset = useMacroStore(state => state.activePreset);
    const isLoading = useMacroStore(state => state.isLoading);

    const { triggerToast, setFadingId } = useUI();

    const getToken = useCallback(async () => {
        if (!currentUser) return null;
        return await currentUser.getIdToken();
    }, [currentUser]);

    const loadMacrosAndExclusions = useCallback(async () => {
        const token = await getToken();
        if (token) await storeLoadMacrosAndExclusions(token);
    }, [getToken, storeLoadMacrosAndExclusions]);

    const handleUpdateMacros = useCallback(async () => {
        const token = await getToken();
        if (!token) return;

        // Mapping/Validation like in App.js
        const payload: Macros = {
            calories: Number(tempMacros.calories || macros.calories),
            protein: Number(tempMacros.protein || macros.protein),
            carbs: Number(tempMacros.carbs || macros.carbs),
            fat: Number(tempMacros.fat || macros.fat),
        };

        try {
            await storeUpdateMacros(token, payload);
            triggerToast("Targets saved!");
        } catch (err) {
            console.error("Failed to update macro goals:", err);
            triggerToast("Failed to update macro goals.");
        }
    }, [getToken, tempMacros, macros, storeUpdateMacros, triggerToast]);

    const handleAddExclusion = useCallback(async () => {
        const token = await getToken();
        if (!token) return;

        try {
            await storeAddExclusion(token, newExclusion);
        } catch (err) {
            triggerToast("Failed to add exclusion.");
        }
    }, [getToken, storeAddExclusion, newExclusion, triggerToast]);

    const handleRemoveExclusion = useCallback(async (item: string) => {
        const token = await getToken();
        if (!token) return;

        // Match the animation logic from App.js
        setFadingId(item);
        await new Promise(r => setTimeout(r, 400));

        try {
            await storeRemoveExclusion(token, item);
            setFadingId(null);
        } catch (err) {
            setFadingId(null);
            triggerToast("Failed to remove exclusion.");
        }
    }, [getToken, storeRemoveExclusion, setFadingId, triggerToast]);

    const handleAutoMacroUpdate = useCallback(async (newMacros: any) => {
        const token = await getToken();
        if (!token) return;

        try {
            await storeAutoUpdateMacros(token, newMacros);
            triggerToast("Macro goals updated successfully!");
            return "Goal updated!"; // Return message for chefMessage in App.js
        } catch (err) {
            return "";
        }
    }, [getToken, storeAutoUpdateMacros, triggerToast]);

    const handleAutoExclusionUpdate = useCallback(async (updates: any) => {
        const token = await getToken();
        if (!token) return;

        try {
            await storeAutoUpdateExclusions(token, updates);
            return "Preferences updated!";
        } catch (err) {
            return "";
        }
    }, [getToken, storeAutoUpdateExclusions]);

    return {
        // State
        macros,
        tempMacros,
        exclusions,
        newExclusion,
        activePreset,
        isLoading,

        // Basic Setters
        setMacros: storeSetMacros,
        setTempMacros: storeSetTempMacros,
        setNewExclusion: storeSetNewExclusion,
        setActivePreset: storeSetActivePreset,

        // High-level Actions
        loadMacrosAndExclusions,
        handleUpdateMacros,
        handleAddExclusion,
        handleRemoveExclusion,
        handleAutoMacroUpdate,
        handleAutoExclusionUpdate,
        handlePresetSelect: useCallback(async (preset: any) => {
            const token = await getToken();
            storeHandlePresetSelect(preset, token || undefined);
        }, [getToken, storeHandlePresetSelect]),
    };
}
